import { useTranslation } from "react-i18next";
import {
  PLATFORM_PLACEMENTS,
  type PreflightPlatformCode,
} from "@nexus/contracts";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from "@nexus/ui";
import { PlatformLabel } from "../PlatformLabel/PlatformLabel";

const PLATFORM_CODES = Object.keys(
  PLATFORM_PLACEMENTS,
) as PreflightPlatformCode[];

export interface PlatformPickerProps {
  /** `null` means no platform chosen — the "all platforms" state where `includeAll` is set. */
  value: PreflightPlatformCode | null;
  onChange: (platform: PreflightPlatformCode | null) => void;
  /** Offers a leading "All" entry, for filters where no platform is a valid answer. */
  includeAll?: boolean;
  /** Shown on the trigger while nothing is chosen; ignored when `includeAll` is set. */
  placeholder?: string;
  /** Filters sit in dense toolbars and size to their content instead of the column. */
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * A menu rather than segments: six platforms with brand marks overflow both half a
 * dialog column and the reports toolbar, and a scrolling segmented track hides the
 * overflowing options with no affordance that they exist (AD-2226).
 */
export function PlatformPicker({
  value,
  onChange,
  includeAll = false,
  placeholder,
  fullWidth = false,
  disabled = false,
  className,
}: PlatformPickerProps) {
  const { t } = useTranslation();

  const allLabel = t("preflight.pastReports.filterAll");
  const emptyLabel =
    placeholder ??
    t("preflight.platformSelect.selectPlaceholder", "Select a platform");

  return (
    // modal={false}: a modal menu inside a Radix Dialog sets body pointer-events:none,
    // which can outlive the menu and leave the page unclickable (AD-2098).
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          iconEnd="chevron-down"
          disabled={disabled}
          className={cn(
            "cursor-pointer justify-between",
            fullWidth ? "w-full" : "shrink-0",
            className,
          )}
          aria-label={t("preflight.platformSelect.selectAria", {
            platform: value
              ? t(`preflight.platform.${value}`)
              : includeAll
                ? allLabel
                : t("preflight.platformSelect.noneSelected", "none selected"),
            defaultValue: "Platform: {{platform}}",
          })}
        >
          {value ? (
            <PlatformLabel platform={value} />
          ) : (
            <span
              className={cn("truncate", !includeAll && "text-muted-foreground")}
            >
              {includeAll ? allLabel : emptyLabel}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        // Matches the trigger so the open menu doesn't jump wider than the field.
        className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {includeAll ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onChange(null);
            }}
            className="flex cursor-pointer items-center gap-2.5"
          >
            <SelectedDot selected={value === null} />
            {allLabel}
          </DropdownMenuItem>
        ) : null}
        {PLATFORM_CODES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={(event) => {
              event.stopPropagation();
              onChange(code);
            }}
            className="flex cursor-pointer items-center gap-2.5"
          >
            <SelectedDot selected={code === value} />
            <PlatformLabel platform={code} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SelectedDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2 shrink-0 rounded-full",
        selected ? "bg-primary" : "bg-muted-foreground/35",
      )}
    />
  );
}
